import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface VehicleAccess {
  id: number;
  access_record_id: number;
  access_record:AccessRecord;
  license_plate: string;
  vehicle_model?: string;
  vehicle_color?: string;
  vehicle_photo_url?: string;
  created_at: Date;
}

export interface AccessRecord {
  id: number;
  company_id: number;
  position_id: number;
  establishment_id?: number;
  guard_id: number;
  visitor_name: string;
  visitor_phone?: string;
  visitor_document?: string;
  visitor_document_type?: string;
  reason?: string;
  authorized_by_contact_id?: number;
  authorized_by_name?: string;
  authorized_by_role?: string;
  authorization_notes?: string;
  visitor_photo_url?: string;
  visitor_document_photo_url?:string;  
  type: 'entry' | 'exit';
  access_time: Date;
  exit_time?: Date;
  status: 'active' | 'exited';
  guard_notes?: string;
  vehicles: VehicleAccess[];
  position?: {
    id: number;
    name: string;
  };
  establishment?: {
    id: number;
    name: string;
  };
  guard?: {
    id: number;
    first_name: string;
    last_name: string;
  };
  authorized_by_contact?: {
    id: number;
    name: string;
    phone: string;
  };
  created_at: Date;
  updated_at: Date;
}

export interface CreateAccessRecordDto {
  position_id: number;
  establishment_id?: number;
  visitor_name: string;
  visitor_phone?: string;
  visitor_document?: string;
  visitor_document_type?: string;
  reason?: string;
  authorized_by_contact_id: number;
  authorized_by_name?: string;
  authorized_by_role?: string;
  authorization_notes?: string;
  visitor_photo_base64?: string;
  visitor_document_photo_base64?:string;
  access_time: Date;
  guard_notes?: string;
  vehicles?: CreateVehicleDto[];
}

export interface CreateVehicleDto {
  license_plate: string;
  vehicle_model?: string;
  vehicle_color?: string;
  vehicle_photo_base64?: string;
}

export interface ExitAccessDto {
  access_record_id: number;
  exit_notes?: string;
}

export interface AccessRecordFilter {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  positionId?: number;
  establishmentId?: number;
  guardId?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  status?: 'active' | 'exited' | 'rejected' | 'pending' |'under_review';
  type?: 'entry' | 'exit';
}

export interface AccessRecordStats {
  total: number;
  active: number;
  exited: number;
  byDay: { date: string; count: number }[];
}

@Injectable({
  providedIn: 'root'
})
export class AccessRecordService {
  private readonly apiUrl = `${environment.apiUrl}/access-records`;

  constructor(private http: HttpClient) {}

  /**
   * Registrar entrada
   */
  create(dto: CreateAccessRecordDto): Observable<AccessRecord> {
    return this.http.post<AccessRecord>(this.apiUrl, dto).pipe(
      // tap(response => console.log('Entrada registrada:', response)),
      catchError(this.handleError)
    );
  }

  /**
   * Registrar saída
   */
  exit(dto: ExitAccessDto): Observable<AccessRecord> {
    return this.http.put<AccessRecord>(`${this.apiUrl}/exit`, dto).pipe(
      // tap(response => console.log('Saída registrada:', response)),
      catchError(this.handleError)
    );
  }

  /**
   * Listar registros com paginação
   */
  findAll(filters?: AccessRecordFilter): Observable<PaginatedResponse<AccessRecord>> {
    const params = this.buildParams(filters);
    return this.http.get<PaginatedResponse<AccessRecord>>(this.apiUrl, { params }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Buscar registro por ID
   */
  findOne(id: number): Observable<AccessRecord> {
    return this.http.get<AccessRecord>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Listar entradas ativas por posto
   */
  getActiveEntries(positionId: number): Observable<AccessRecord[]> {
    return this.http.get<AccessRecord[]>(`${this.apiUrl}/position/${positionId}/active`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Listar registros por posto
   */
  findByPosition(positionId: number, limit: number = 50): Observable<AccessRecord[]> {
    return this.http.get<AccessRecord[]>(`${this.apiUrl}/position/${positionId}`, {
      params: new HttpParams().set('limit', limit.toString())
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Listar registros por estabelecimento
   */
  findByEstablishment(establishmentId: number, limit: number = 50): Observable<AccessRecord[]> {
    return this.http.get<AccessRecord[]>(`${this.apiUrl}/establishment/${establishmentId}`, {
      params: new HttpParams().set('limit', limit.toString())
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Obter estatísticas
   */
  getStats(positionId?: number, startDate?: string, endDate?: string): Observable<AccessRecordStats> {
    let params = new HttpParams();
    if (positionId) params = params.set('positionId', positionId.toString());
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    
    return this.http.get<AccessRecordStats>(`${this.apiUrl}/stats`, { params }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Construir parâmetros HTTP
   */
  private buildParams(filters?: AccessRecordFilter): HttpParams {
    let params = new HttpParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params = params.append(key, value.toString());
        }
      });
    }
    
    return params;
  }

  /**
   * Tratamento de erros
   */
  private handleError(error: any): Observable<never> {
    console.error('Erro no AccessRecordService:', error);
    const message = error.error?.message || error.message || 'Erro desconhecido';
    return throwError(() => new Error(message));
  }
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}